// src/pages/Dashboard/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { RefreshCw, Activity } from 'lucide-react';
import {
    CallQualificationDistributionBarGraph,
    CallTransferDistributionBarGraph,
    CallStatusDistributionBarGraph,
    SalesFunnelArea,
    AIvsHumanTimeBarGraph,
} from 'components/charts';
import { useGetDashboardData } from 'services/hooks/queries';
import type { QuickRangeId } from 'common/components';
import { InfoCards, TotalCallTime, TotalAITime } from 'components';
import { DashboardDateRangeSelector, WorkflowOptions } from './components';
import { isTodayQuickRange, quickRangeIdToDateRangeType } from './dashboardDateRange.utils';
import styles from './Dashboard.module.scss';
import { Button, Select } from 'common/components';
import { CallDirection, CallDirectionType } from 'types/call.type';

const Dashboard: React.FC = () => {
    // Date range state
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [quickRangeId, setQuickRangeId] = useState<QuickRangeId | null>('today');

    const [selectedWorkflow, setSelectedWorkflow] = useState<string>('All Workflows');
    const [callDirection, setCallDirection] = useState<CallDirectionType>(CallDirection.ALL);

    // State for API data
    const [error, setError] = useState<Error | null>(null);

    const { dashboardData, isDashboardDataLoading, dashboardDataError, refetchDashboardData } = useGetDashboardData({
        startDate,
        endDate,
        selectedWorkflow,
        quickRangeId,
        callDirection,
    });

    const chartDateRangeType = quickRangeIdToDateRangeType(quickRangeId);

    useEffect(() => {
        if (dashboardDataError) {
            setError(dashboardDataError);
        }
    }, [dashboardDataError]);

    const isRefetchingActive = isTodayQuickRange(quickRangeId);

    return (
        <div className={styles.dashboardContainer}>
            {/* Dashboard Header and Date Range Selector */}
            <div className={styles.dashboardFilters}>
                <div className={styles.dateRangeDisplay}>
                    <div className={styles.activityIconContainer}>
                        <Activity
                            size={16}
                            className={`${isRefetchingActive ? styles.animatePulse : styles.textGray}`}
                        />
                        <div className={styles.tooltip}>
                            Auto-refresh: Active for Today only (every 5 minutes). Historical dates use static data -
                            manual refresh available.
                        </div>
                    </div>

                    <h3>Dashboard Overview</h3>
                </div>

                <div className={styles.controlsWrapper}>
                    {/* Workflow Filter */}
                    <WorkflowOptions
                        selectedWorkflow={selectedWorkflow}
                        onWorkflowChange={setSelectedWorkflow}
                        dashboardData={dashboardData}
                        isDashboardDataLoading={isDashboardDataLoading}
                    />

                    <DashboardDateRangeSelector
                        startDate={startDate}
                        endDate={endDate}
                        onChange={({ startDate: newStartDate, endDate: newEndDate, quickRangeId: newQuickRangeId }) => {
                            setQuickRangeId(newQuickRangeId);
                            setStartDate(newStartDate);
                            setEndDate(newEndDate);
                        }}
                    />

                    {/* Call Direction Filter */}
                    <div style={{ minWidth: 180 }}>
                        <Select
                            value={callDirection}
                            onChange={(e) => setCallDirection(e.target.value as CallDirectionType)}
                            options={[
                                { value: CallDirection.ALL, label: 'All Calls' },
                                { value: CallDirection.OUTBOUND, label: 'Outbound' },
                                { value: CallDirection.INBOUND, label: 'Inbound' },
                            ]}
                        />
                    </div>

                    <div className={styles.mlAuto}>
                        <Button
                            variant='outline-secondary'
                            onClick={() => refetchDashboardData()}
                            icon={RefreshCw}
                            className={styles.ctaButton}
                            isIconSpinning={isDashboardDataLoading}
                        >
                            <span>Refresh Data</span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Loading and Error States */}
            {isDashboardDataLoading && (
                <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                </div>
            )}

            {error && <p className={styles.errorMessage}>{error.message || 'An error occurred'}</p>}

            {!isDashboardDataLoading && !error && dashboardData && (
                <>
                    {/* Info Cards Row */}
                    <div className={styles.infoCardsRow}>
                        {dashboardData.infos && <InfoCards infos={dashboardData.infos} />}
                        {dashboardData.charts?.callTimeDistributionBar && (
                            <TotalCallTime callData={dashboardData.charts.callTimeDistributionBar} />
                        )}
                        {dashboardData.charts?.callTimeDistributionBar && (
                            <TotalAITime callData={dashboardData.charts.callTimeDistributionBar} />
                        )}
                    </div>

                    {/* Top Charts Row */}
                    <div className={styles.chartsRow}>
                        {/* Call Status Distribution */}
                        {!!dashboardData.charts?.callStatusDistributionBar?.dataPoints?.length && (
                            <CallStatusDistributionBarGraph
                                data={dashboardData.charts.callStatusDistributionBar.dataPoints}
                                amountofCalls={dashboardData.charts.callStatusDistributionBar.totalCount}
                                height={150}
                                dateRangeType={chartDateRangeType}
                                startDate={startDate}
                                endDate={endDate}
                                title='Call Status Overview'
                            />
                        )}

                        {/* Call Transfer Distribution */}
                        {!!dashboardData.charts?.callTransferDistributionBar?.dataPoints?.length && (
                            <CallTransferDistributionBarGraph
                                data={dashboardData.charts.callTransferDistributionBar.dataPoints}
                                amountofCalls={dashboardData.charts.callTransferDistributionBar.totalCount}
                                dateRangeType={chartDateRangeType}
                                startDate={startDate}
                                endDate={endDate}
                                title='Call Transfer Distribution'
                            />
                        )}
                    </div>

                    {/* Middle Charts Row */}
                    <div className={styles.chartsRow}>
                        {/* Call Qualification Distribution */}
                        {!!dashboardData.charts?.callQualificationDistributionBar?.dataPoints?.length && (
                            <CallQualificationDistributionBarGraph
                                data={dashboardData.charts.callQualificationDistributionBar.dataPoints}
                                amountofCalls={dashboardData.charts.callQualificationDistributionBar.totalCount}
                                dateRangeType={chartDateRangeType}
                                startDate={startDate}
                                endDate={endDate}
                                title='Call Qualification Distribution'
                            />
                        )}

                        {/* AI vs Human Time Bar Graph */}
                        {!!dashboardData.charts?.callTimeDistributionBar?.totalCount && (
                            <AIvsHumanTimeBarGraph
                                data={dashboardData.charts.callTimeDistributionBar}
                                height={250}
                                title='AI vs Human Time'
                            />
                        )}
                    </div>

                    {/* Sales Funnel Row - Full Width */}
                    {!!dashboardData.charts?.salesFunnelArea?.totalCount && (
                        <div className={styles.salesFunnelRow}>
                            <div className={styles.chartCard}>
                                <SalesFunnelArea
                                    data={dashboardData.charts.salesFunnelArea}
                                    height={250}
                                    title='Sales Conversion Funnel'
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Dashboard;
